<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { isNull } from 'lodash-es';
    import SimpleSelect from 'portlet/common/components/SimpleSelect.svelte';
    import type { ViewGroupMember } from 'portlet/models';
    import type { InteractiveTitleSelectCurrentViewDefinition } from '../../types';

    interface Props {
        field: InteractiveTitleSelectCurrentViewDefinition;
    }

    let { field }: Props = $props();

    let options = $derived(field.options);
    let items = $derived(
        options
            .flatMap(group => {
                return isNull(group.name) || group.members.length < 2
                    ? group.members
                    : [{ id: null, label: group.name }, ...group.members];
            })
            .map(({ id: value, label }) => ({
                label,
                value,
            })),
    );
    let value = $derived(items.filter(item => item.value === field.value));

    const dispatch = createEventDispatcher();

    const onChange = ({ label, value }: { label: string; value: number | null }) => {
        dispatch('change', {
            ...field,
            value: isNull(value) ? persistViewForm(label) : value,
        });
    };

    const persistViewForm = (groupName: string): number => {
        const newGroup = options.find(group => group.name === groupName);
        if (!newGroup) return field.value;
        const fellowView = newGroup.members.find(
            view => view.form?.toLowerCase() === findView(field.value)?.form?.toLowerCase(),
        );
        if (!fellowView) return newGroup.defaultView;
        return fellowView.id;
    };

    const isGroupName = isNull;

    const isGroupMember = (viewId: number | null): boolean => {
        if (!viewId) return false;
        const groupSize = options.find(group => group.members.map(({ id }) => id).includes(viewId))
            ?.members.length;
        return !!groupSize && groupSize > 1;
    };

    const findView = (viewId: number): ViewGroupMember | undefined => {
        return options
            .map(x => x.members)
            .flat()
            .find(({ id }) => id === viewId);
    };
</script>

<span class="interactive-title-current-view">
    <SimpleSelect {items} {value}>
        <li
            class="option"
            slot="item"
            class:selected
            class:group-name={isGroupName(item.value)}
            class:group-member={isGroupMember(item.value)}
            onclick={() => onChange(item)}
            let:item
            let:selected
        >
            {item.label}
        </li>
    </SimpleSelect>
</span>

<style lang="scss" scoped>
    .interactive-title-current-view {
        //overwriting SimpleSelect component styles so its fit in portlet title
        :global(div.select) {
            display: inline !important;
            :global(button.input) {
                border: none !important;
                background-color: #e9edf3 !important;
                padding: 4px 8px !important;
                &::after {
                    display: none !important;
                }
            }
        }
    }
    li.option {
        display: block;
        padding: 3px 4px;
        overflow: hidden;
        text-align: left;
        white-space: nowrap;
        font-size: 0.8rem;

        &:hover,
        &:focus {
            outline: none;
            background: #f2f2f2;
        }
        &.selected {
            font-weight: bold;
            background-color: #f2f2f2;
        }
        &.group-name {
            color: #555;
        }
        &.group-member {
            padding-left: 1rem;
        }
    }
</style>
